import { useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Bloom, EffectComposer } from '@react-three/postprocessing';

import NegativeX from 'assets/images/cubemaps/GalaxyTex_NegativeX.jpg';
import NegativeY from 'assets/images/cubemaps/GalaxyTex_NegativeY.jpg';
import NegativeZ from 'assets/images/cubemaps/GalaxyTex_NegativeZ.jpg';
import PositiveX from 'assets/images/cubemaps/GalaxyTex_PositiveX.jpg';
import PositiveY from 'assets/images/cubemaps/GalaxyTex_PositiveY.jpg';
import PositiveZ from 'assets/images/cubemaps/GalaxyTex_PositiveZ.jpg';
import * as THREE from 'three';

const STAR_COUNT = 1200;
const FIELD_DEPTH = 30;
const FIELD_RADIUS = 25;
const MAX_TRAIL_SCALE = 30;

interface StarData {
  baseSpeed: number;
  // Base tint color (HDR, >1.0 for bloom)
  r: number;
  g: number;
  b: number;
}

const tempObject = new THREE.Object3D();
const tempColor = new THREE.Color();

// Random tint for a star (muted, not bright)
const randomTint = (): [number, number, number] => {
  const tint = Math.random();
  if (tint < 0.45) {
    // White-bluish
    return [1.2 + Math.random() * 0.3, 1.25 + Math.random() * 0.25, 1.4 + Math.random() * 0.2];
  } else if (tint < 0.6) {
    // Warm yellow
    return [1.4 + Math.random() * 0.2, 1.2 + Math.random() * 0.2, 0.6 + Math.random() * 0.2];
  } else if (tint < 0.75) {
    // Soft pink
    return [1.4 + Math.random() * 0.2, 0.8 + Math.random() * 0.2, 1.0 + Math.random() * 0.2];
  } else if (tint < 0.88) {
    // Pale green
    return [0.7 + Math.random() * 0.2, 1.3 + Math.random() * 0.2, 0.7 + Math.random() * 0.2];
  } else {
    // Cool blue
    return [0.7 + Math.random() * 0.15, 0.9 + Math.random() * 0.2, 1.4 + Math.random() * 0.2];
  }
};

const Skybox = () => {
  const { scene } = useThree();

  useMemo(() => {
    const loader = new THREE.CubeTextureLoader();
    const texture = loader.load([PositiveX, NegativeX, PositiveY, NegativeY, PositiveZ, NegativeZ]);
    scene.background = texture;
  }, [scene]);

  return null;
};

const Stars = () => {
  const meshRef = useRef<THREE.InstancedMesh>(null);

  const shiftRef = useRef(false);

  const stateRef = useRef({
    speed: 1.0,
    targetSpeed: 1.0,
  });

  // Per-star data: position stored separately for fast access
  const starsRef = useRef<{ x: number; y: number; z: number; data: StarData }[]>([]);

  // Initialize star positions and colors
  useMemo(() => {
    const stars: { x: number; y: number; z: number; data: StarData }[] = [];

    for (let i = 0; i < STAR_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * FIELD_RADIUS;
      const [cr, cg, cb] = randomTint();

      stars.push({
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r,
        z: (Math.random() - 0.5) * FIELD_DEPTH,
        data: {
          baseSpeed: 0.4 + Math.random() * 0.6,
          r: cr,
          g: cg,
          b: cb,
        },
      });
    }

    starsRef.current = stars;
  }, []);

  // Set initial instance matrices and colors
  useEffect(() => {
    if (!meshRef.current) return;
    const mesh = meshRef.current;
    const stars = starsRef.current;

    for (let i = 0; i < STAR_COUNT; i++) {
      const star = stars[i];
      tempObject.position.set(star.x, star.y, star.z);
      tempObject.scale.set(1, 1, 1);
      tempObject.updateMatrix();
      mesh.setMatrixAt(i, tempObject.matrix);
      tempColor.setRGB(star.data.r, star.data.g, star.data.b);
      mesh.setColorAt(i, tempColor);
    }

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') shiftRef.current = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') shiftRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useFrame((_, delta) => {
    if (!meshRef.current) return;

    const shift = shiftRef.current;
    const state = stateRef.current;
    const mesh = meshRef.current;
    const stars = starsRef.current;
    const halfDepth = FIELD_DEPTH / 2;

    // Speed control
    if (shift) {
      state.targetSpeed = 8.0;
    } else {
      state.targetSpeed = 1.0;
    }

    const speedLerp = shift ? 0.05 : 0.03;
    state.speed += (state.targetSpeed - state.speed) * speedLerp;

    // Trail length proportional to speed
    const trailScale = Math.max(1, state.speed * (MAX_TRAIL_SCALE / 8));

    const baseMovement = 15 * delta;

    for (let i = 0; i < STAR_COUNT; i++) {
      const star = stars[i];
      const starSpeed = star.data.baseSpeed * state.speed;

      // Move toward camera (+z)
      star.z += baseMovement * starSpeed;

      // Wrap around seamlessly
      if (star.z > halfDepth) {
        const angle = Math.random() * Math.PI * 2;
        const r = Math.sqrt(Math.random()) * FIELD_RADIUS;
        star.x = Math.cos(angle) * r;
        star.y = Math.sin(angle) * r;
        star.z = -halfDepth;
      }

      // Position + trail stretch
      tempObject.position.set(star.x, star.y, star.z);
      tempObject.scale.set(1, 1, trailScale);
      tempObject.updateMatrix();
      mesh.setMatrixAt(i, tempObject.matrix);

      // Depth-based brightness: far = dim, close = bright
      const depthFactor = (star.z + halfDepth) / FIELD_DEPTH; // 0 (far) → 1 (close)
      const brightness = 0.15 + depthFactor * 0.85;
      tempColor.setRGB(
        star.data.r * brightness,
        star.data.g * brightness,
        star.data.b * brightness
      );
      mesh.setColorAt(i, tempColor);
    }

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  });

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, STAR_COUNT]}>
      <sphereGeometry args={[0.02, 4, 4]} />
      <meshBasicMaterial toneMapped={false} />
    </instancedMesh>
  );
};

export const Space = () => {
  return (
    <Canvas
      camera={{ position: [0, 0, 0], fov: 100, near: 0.01, far: 200 }}
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
    >
      <Skybox />
      <Stars />
      <EffectComposer>
        <Bloom intensity={0.8} luminanceThreshold={0.2} luminanceSmoothing={0.9} mipmapBlur />
      </EffectComposer>
    </Canvas>
  );
};
